"use strict";
// ================================================================
// v2/theme/createV2Palette.ts
// デザイナートークン (tokens/colors) から theme.palette.v2 を生成する。
// utoniq-app の defaultLightTheme / defaultDarkTheme から呼ばれ、
// MUI テーマに additive に統合される (既存 palette には触れない)。
//
// アプリ毎のブランドカラー適用は withV2BrandColors が担う。
// アプリ色は GCS の theme.json を AppThemeProvider が deepmerge するが、
// theme.json 側に v2 キーが無いため palette.v2 は上書きされない。
// そこで useV2Palette が palette.primary を読んで primary 系だけ差し替える。
// ================================================================
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createV2Palette = createV2Palette;
exports.withV2BrandColors = withV2BrandColors;
exports.withV2NeutralColors = withV2NeutralColors;
var material_1 = require("@mui/material");
var colorUtils_1 = require("../../utils/colorUtils");
var colors_1 = require("../tokens/colors");
/** Figma BrandShadow: inner(0,1) + drop(0,4)/blur20 */
var brandShadow = function (color) {
    return "inset 0px 1px 0px 0px ".concat(color, ", 0px 4px 20px 0px ").concat(color);
};
/** BrandShadow の不透明度 (Figma Effect/Shadow/Brand = primary の 32%) */
var BRAND_SHADOW_ALPHA = '52';
/**
 * モードに応じた v2 palette スライスを生成する。
 *
 * @example
 *   // defaultLightTheme.ts
 *   import { v2 } from 'utoniq-ui'
 *   palette: { ...palette, v2: v2.createV2Palette('light') }
 */
function createV2Palette(mode) {
    var c = colors_1.v2ColorsByMode[mode];
    // トークンはモジュールスコープの singleton なので、参照をそのまま返すと
    // palette.v2 を破壊的に書き換える処理があったときにトークン自体が汚れる。
    // ここでコピーするのは最上位の 4 グループだけで、その下のネスト
    // (background.primary など) は共有参照のまま。完全な保護ではないので、
    // palette.v2 へ in-place で書き込む処理は足さないこと
    // (現状 theme.json のマージは deepmerge パッケージで、ソースは変更されない)。
    return {
        background: __assign({}, c.background),
        foreground: __assign({}, c.foreground),
        border: __assign({}, c.border),
        unique: __assign({}, c.unique),
        shadow: (function () {
            // カード上部に乗せる透かし線
            var cardHighlight = "inset 0px 1px 0px 0px ".concat(c.shadowColor.innerCard);
            // カード周辺の影
            var cardDrop = "0px 8px 32px 0px ".concat(c.shadowColor.card);
            return {
                // Figma CardShadow: inner(0,1) + drop(0,8)/blur32。cardGlass と同一の影で、
                // 差分は backdrop-blur の有無のみ (blur は利用側の CSS で付与)。
                card: "".concat(cardHighlight, ", ").concat(cardDrop),
                // Figma Card: CardShadow と同じ影 + backdrop-blur(利用側で付与)。
                cardGlass: "".concat(cardHighlight, ", ").concat(cardDrop),
                cardHighlight: cardHighlight,
                cardDrop: cardDrop,
                // Figma BrandShadow: inner(0,1) + drop(0,4)/blur20。
                brand: brandShadow(c.shadowColor.brand),
            };
        })(),
    };
}
var mergeScale = function (base, override) {
    var _a, _b;
    if (!(override === null || override === void 0 ? void 0 : override.main) && !(override === null || override === void 0 ? void 0 : override.light))
        return base;
    return {
        default: (_a = override.main) !== null && _a !== void 0 ? _a : base.default,
        subtle: (_b = override.light) !== null && _b !== void 0 ? _b : base.subtle,
    };
};
/**
 * v2 palette の primary/secondary/info/warning/error 系を、アプリのブランドカラーで差し替える。
 *
 * 差し替え対象 (Figma の primary トークンを使っている箇所):
 *   - background.primary.default … アクティブタブ / primary ボタン / カート件数バッジ
 *   - background.primary.subtle  … Badge color="primary" の面 (会場受け取り 等)
 *   - foreground.primary.main    … カード左アクセントバー / メンバーシップ限定
 *   - shadow.brand               … アクティブタブのブランド影
 * secondary/info/warning/error も同様に background.<name> / foreground.semantic.<name> /
 * border.semantic.<name> を差し替える (foreground/border.semantic.success は
 * secondary から反映する。v2 の既定値で success === secondary.default と
 * 一致しており、v1 側にも success という独立したキーは存在しないため)。
 *
 * neutral 系 (文字色・境界線・カード面) は withV2NeutralColors が別途担当する。
 *
 * 前提: primary.light は primary.main の淡色であること。
 * background.primary.subtle は Badge(subtle/primary) の面として使い、その上に
 * foreground.primary.main (= primary.main) を載せるため、2 色の色相がずれると
 * 読めなくなる。AppThemeProvider は theme.json を既定テーマに deepmerge する
 * ので、アプリが primary.main だけを定義すると primary.light は既定テーマの
 * 値が残る。アプリの theme.json では main と light を必ず対で定義すること。
 *
 * ダークモードでは primary.light を subtle に流用しない。MUI の primary.light は
 * ライト面に載せる淡色として定義されており (既定のダークテーマも #E2F1FE の
 * ような明るい値を持つ)、そのまま暗い背景に置くと白い面として浮くため。
 * ダーク時は base (createV2Palette('dark')) の subtle を維持する。
 *
 * @param base v2 パレット (テーマ注入済み or createV2Palette の戻り値)
 * @param primary アプリのブランドカラー。未指定なら base をそのまま返す
 * @param semantic secondary/info/warning/error。未指定ならそれぞれ base をそのまま使う
 * @param customOnPrimary primary 塗り面に乗せる文字色の明示的な指定
 *   (palette.custom.onPrimary)。未指定なら base の既定値 (白) をそのまま使う
 * @param mode テーマのモード。'dark' のとき subtle の差し替えを行わない
 */
function withV2BrandColors(base, primary, semantic, customOnPrimary, mode) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    if (mode === void 0) { mode = 'light'; }
    var main = primary === null || primary === void 0 ? void 0 : primary.main;
    // ダークでは primary.light を面として使えないので、既定値のままにする
    var light = mode === 'dark' ? undefined : primary === null || primary === void 0 ? void 0 : primary.light;
    if (!main && !light)
        return base;
    var primaryDefault = main !== null && main !== void 0 ? main : base.background.primary.default;
    return __assign(__assign({}, base), { background: __assign(__assign({}, base.background), { primary: {
                default: primaryDefault,
                subtle: light !== null && light !== void 0 ? light : base.background.primary.subtle,
            }, secondary: mergeScale(base.background.secondary, semantic === null || semantic === void 0 ? void 0 : semantic.secondary), info: mergeScale(base.background.info, semantic === null || semantic === void 0 ? void 0 : semantic.info), warning: mergeScale(base.background.warning, semantic === null || semantic === void 0 ? void 0 : semantic.warning), error: mergeScale(base.background.error, semantic === null || semantic === void 0 ? void 0 : semantic.error) }), foreground: __assign(__assign({}, base.foreground), { primary: __assign(__assign({}, base.foreground.primary), { main: main !== null && main !== void 0 ? main : base.foreground.primary.main, onPrimary: customOnPrimary !== null && customOnPrimary !== void 0 ? customOnPrimary : base.foreground.primary.onPrimary }), semantic: __assign(__assign({}, base.foreground.semantic), { info: (_b = (_a = semantic === null || semantic === void 0 ? void 0 : semantic.info) === null || _a === void 0 ? void 0 : _a.main) !== null && _b !== void 0 ? _b : base.foreground.semantic.info, warning: (_d = (_c = semantic === null || semantic === void 0 ? void 0 : semantic.warning) === null || _c === void 0 ? void 0 : _c.main) !== null && _d !== void 0 ? _d : base.foreground.semantic.warning, error: (_f = (_e = semantic === null || semantic === void 0 ? void 0 : semantic.error) === null || _e === void 0 ? void 0 : _e.main) !== null && _f !== void 0 ? _f : base.foreground.semantic.error, success: (_h = (_g = semantic === null || semantic === void 0 ? void 0 : semantic.secondary) === null || _g === void 0 ? void 0 : _g.main) !== null && _h !== void 0 ? _h : base.foreground.semantic.success }) }), border: __assign(__assign({}, base.border), { semantic: __assign(__assign({}, base.border.semantic), { info: (_k = (_j = semantic === null || semantic === void 0 ? void 0 : semantic.info) === null || _j === void 0 ? void 0 : _j.main) !== null && _k !== void 0 ? _k : base.border.semantic.info, warning: (_m = (_l = semantic === null || semantic === void 0 ? void 0 : semantic.warning) === null || _l === void 0 ? void 0 : _l.main) !== null && _m !== void 0 ? _m : base.border.semantic.warning, error: (_p = (_o = semantic === null || semantic === void 0 ? void 0 : semantic.error) === null || _o === void 0 ? void 0 : _o.main) !== null && _p !== void 0 ? _p : base.border.semantic.error, success: (_r = (_q = semantic === null || semantic === void 0 ? void 0 : semantic.secondary) === null || _q === void 0 ? void 0 : _q.main) !== null && _r !== void 0 ? _r : base.border.semantic.success }) }), shadow: __assign(__assign({}, base.shadow), { 
            // アルファを足せる #RRGGBB のときだけ影も作り直す
            brand: (0, colorUtils_1.isHex6)(main)
                ? brandShadow("".concat(main).concat(BRAND_SHADOW_ALPHA))
                : base.shadow.brand }) });
}
/**
 * v2 palette の中立色 (背景・文字・境界線) を、アプリ/アーティストの
 * theme.json が上書きした MUI 標準の grey / text に合わせて差し替える。
 *
 * 背景: アーティストは palette.mode を変えず、grey / text / background を
 * 直接暗い値に上書きすることで「ダークに見えるページ」を作っている
 * (例: apps/aqours の一部アーティストは grey.50 を #181F22、text.primary を
 * #FFFFFF に上書き)。v1 は 'grey.50' 等の MUI 標準トークンをライブ参照する
 * ため自動で追従するが、v2 の中立色はビルド時に mode だけを見て固定される
 * ため追従しなかった。ここで grey/text を渡すと、v1 と同じ土台の色を
 * 使いつつ、ガラス面 (sub/surface) の半透明+backdrop-blur という v2 独自の
 * 見た目はそのまま維持する (透過をかける元の色だけ差し替える)。
 *
 * @param grey theme.palette.grey (MUI 標準の 50〜900 スケール)
 * @param text theme.palette.text (MUI 標準 + このアプリ拡張の hint)
 */
function withV2NeutralColors(base, grey, text) {
    var _a, _b, _c, _d;
    if (!grey && !text)
        return base;
    var grey50 = grey === null || grey === void 0 ? void 0 : grey[50];
    var grey100 = grey === null || grey === void 0 ? void 0 : grey[100];
    var grey200 = grey === null || grey === void 0 ? void 0 : grey[200];
    return __assign(__assign({}, base), { background: __assign(__assign({}, base.background), { base: grey50 !== null && grey50 !== void 0 ? grey50 : base.background.base, sub: grey50 ? (0, material_1.alpha)(grey50, 0.55) : base.background.sub, surface: grey50 ? (0, material_1.alpha)(grey50, 0.8) : base.background.surface, neutral: {
                default: grey100 !== null && grey100 !== void 0 ? grey100 : base.background.neutral.default,
                subtle: grey50 !== null && grey50 !== void 0 ? grey50 : base.background.neutral.subtle,
            } }), foreground: __assign(__assign({}, base.foreground), { neutral: __assign(__assign({}, base.foreground.neutral), { primary: (_a = text === null || text === void 0 ? void 0 : text.primary) !== null && _a !== void 0 ? _a : base.foreground.neutral.primary, secondary: (_b = text === null || text === void 0 ? void 0 : text.secondary) !== null && _b !== void 0 ? _b : base.foreground.neutral.secondary, hint: (_c = text === null || text === void 0 ? void 0 : text.hint) !== null && _c !== void 0 ? _c : base.foreground.neutral.hint, disabled: (_d = text === null || text === void 0 ? void 0 : text.disabled) !== null && _d !== void 0 ? _d : base.foreground.neutral.disabled }) }), border: __assign(__assign({}, base.border), { neutral: {
                default: grey100 !== null && grey100 !== void 0 ? grey100 : base.border.neutral.default,
                strong: grey200 !== null && grey200 !== void 0 ? grey200 : base.border.neutral.strong,
            } }) });
}
